import { DateTime } from 'luxon'
import { ReactNode, memo, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import ConfirmPopup from '@components/ConfirmPopup'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'
import alertsApi from '@modules/alerts/api'
import { AlertFrequencyType, ProfileAlert } from '@modules/alerts/types'
import shortlistApi from '@modules/shortlist/api'
import ProfileTitleCell from '@modules/shortlist/components/ShortlistTable/ProfileTitleCell'
import { Box } from '@mui/material'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { dateTimeFormats } from '@theme/variables'

import { StyledTableCell, StyledTableRow, TableCellInner, TableWrapper } from '../../index.styles'
import { ManageTableConfigProps, ManageTableProps, ManageTableRowProps, TableData } from '../../types'
import { AlertFrequencySelect } from '../AlertFrequencySelect'
import { ManageTableColumnHeaders } from './ManageTableColumnHeaders'
import { tableColumnsConfig } from './constants'

const getManageTableColumns = (props: ManageTableRowProps) => {
  const { data, t, selectedProfiles, onSelectProfile, onUpdateAlert, onClickProfile } = props

  const tableColumnsComponents: Record<ManageTableConfigProps['fieldName'], (data: TableData) => ReactNode> = {
    profileTitle: (profile: TableData) => (
      <ProfileTitleCell
        profileData={profile}
        isPrivate={profile.profileImgData?.isPrivate}
        isSelectedProfile={selectedProfiles.includes(profile.profileId)}
        onSelectProfile={onSelectProfile}
        isDisabled={profile.profileStatus !== 'active'}
        isDraggable={false}
        showCompanyName
        onClickProfile={onClickProfile}
      />
    ),
    profileCreatedDate: (profile: TableData) => (
      <div>{DateTime.fromISO(profile.profileCreatedDate).toFormat(dateTimeFormats.eventDateFormat)}</div>
    ),
    profileUpdatedDate: (profile: TableData) => (
      <div>
        {DateTime.fromISO(profile.alertUpdatedAt ?? profile.profileUpdatedDate).toFormat(dateTimeFormats.eventDateFormat)}
      </div>
    ),
    frequency: (profile: TableData) => (
      <AlertFrequencySelect
        onSelect={e => {
          onUpdateAlert(
            { id: profile.globalId, type: profile.profileType, alertFrequency: profile.frequency as AlertFrequencyType },
            e.target.value as AlertFrequencyType,
          )
        }}
        title={t(translationKeys.ADD_EMAIL_ALERTS_TITLE)}
        disabled={data?.profileStatus !== 'active'}
        selectedAlertFrequency={profile.frequency as AlertFrequencyType}
      />
    ),
  }

  return (
    <StyledTableRow $isActive={data.profileStatus === 'active'}>
      {tableColumnsConfig.map((tableColumn: ManageTableConfigProps) => {
        const { restCellProps, fieldName } = tableColumn

        const cellComponent = tableColumnsComponents[fieldName]

        return (
          <StyledTableCell
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...restCellProps}
            key={fieldName as string}
            data-testid={`${String(fieldName)}_testid`}
          >
            <TableCellInner data-testid={`${String(fieldName)}_testid_inner`}>
              {cellComponent ? cellComponent(data) : (data?.[fieldName as keyof TableData] as unknown as string)}
            </TableCellInner>
          </StyledTableCell>
        )
      })}
    </StyledTableRow>
  )
}

const ManageTable = (props: ManageTableProps) => {
  const { t } = useTranslation()
  const {
    isPageDataLoading = false,
    pageData,
    sort,
    onSortChange,
    setIsConfirmAlertsRemovePopupShown,
    isConfirmAlertsRemovePopupShown,
    selectedProfiles,
    setSelectedProfiles,
    alertsListId,
    onClickProfile,
  } = props

  const queryClient = useQueryClient()

  const { trackActivity } = useTrackUserActivity()

  const { mutate: deleteProfilesAlerts } = useMutation({
    mutationFn: alertsApi.deleteProfilesAlerts.fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, alertsListId] })

      setSelectedProfiles([])
      setIsConfirmAlertsRemovePopupShown(false)
    },
  })

  const handleSelectProfile = (id: number) => {
    if (selectedProfiles.includes(id)) {
      setSelectedProfiles(profiles => profiles.filter(selectedId => selectedId !== id))
      return
    }
    setSelectedProfiles(profiles => [...profiles, id])
  }

  const handleUpdateProfileAlert = (
    profile: Pick<ProfileAlert, 'id' | 'type' | 'alertFrequency'>,
    frequency?: AlertFrequencyType,
  ) => {
    trackActivity(EVENT_ACTIVITY_KEYS.ALERTS.EMAIL.ADD, {
      profile_type: profile.type,
      new_alert_frequency: frequency || AlertFrequencyType.NO_NOTIFICATION,
      previous_alert_frequency: profile.alertFrequency,
    })

    createProfilesAlert({
      profileAlerts: [{ id: profile.id, type: profile.type }],
      alertFrequency: frequency === AlertFrequencyType.NO_NOTIFICATION ? undefined : frequency,
    })
  }

  const handleRemoveAlerts = () => {
    const profileAlertsToDelete = selectedProfiles.map(id => {
      const profileToDelete = pageData.find(i => i.profileId === id)
      return { id: profileToDelete?.globalId, '@type': profileToDelete?.profileType }
    })

    trackActivity(EVENT_ACTIVITY_KEYS.ALERTS.DELETE_MODAL.DELETE, {
      profile_ids: profileAlertsToDelete.map(p => p.id),
    })
    deleteProfilesAlerts(profileAlertsToDelete)
  }

  const allProfilesSelected = useMemo(() => {
    return pageData.length > 0 && pageData.every(i => selectedProfiles.includes(i.profileId))
  }, [selectedProfiles, pageData])

  const handleSelectAllProfiles = (allSelected: boolean) => {
    setSelectedProfiles(allSelected ? pageData.map(p => p.profileId) : [])
  }
  const { mutate: createProfilesAlert } = useMutation({
    mutationFn: alertsApi.createProfileAlert.fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key] }),
  })

  const tableElementRef = useRef<HTMLTableElement>(null)

  const removeAlertsTexts = useMemo(() => {
    if (selectedProfiles?.length > 1) {
      return {
        title: t(translationKeys.DELETE_ALERTS_FROM_LIST_TITLE),
        text: t(translationKeys.DELETE_ALERTS_FROM_LIST_SUBTITLE),
      }
    }
    return { title: t(translationKeys.DELETE_ALERT_FROM_LIST_TITLE), text: t(translationKeys.DELETE_ALERT_FROM_LIST_SUBTITLE) }
  }, [t, selectedProfiles?.length])

  return (
    <Box sx={{ position: 'relative', overflowX: 'auto', overflowY: 'hidden' }}>
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          minWidth: '600px',
          marginBottom: '-16px',
        }}
      >
        <TableWrapper>
          <TableContainer
            component={Paper}
            sx={{ position: 'relative', boxShadow: 'none', overflowX: 'inherit', paddingBottom: '15px' }}
          >
            <Table ref={tableElementRef}>
              {isPageDataLoading ? (
                <TableBody>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <>
                  <TableHead>
                    <ManageTableColumnHeaders
                      tableColumns={tableColumnsConfig}
                      onSelectAllProfiles={handleSelectAllProfiles}
                      allProfilesSelected={allProfilesSelected}
                      sort={sort}
                      onSortChange={onSortChange}
                    />
                  </TableHead>
                  <TableBody sx={{ position: 'relative' }}>
                    {pageData.map((data: TableData) =>
                      getManageTableColumns({
                        data,
                        t,
                        onSelectProfile: handleSelectProfile,
                        selectedProfiles,
                        onUpdateAlert: handleUpdateProfileAlert,
                        onClickProfile,
                      }),
                    )}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
        </TableWrapper>
      </Box>
      {!!isConfirmAlertsRemovePopupShown && (
        <ConfirmPopup
          id="alerts-delete-confirm"
          isOpen
          width={225}
          title={removeAlertsTexts.title}
          text={removeAlertsTexts.text}
          cancelBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
          confirmBtnText={t(translationKeys.REMOVE)}
          onCancel={() => {
            trackActivity(EVENT_ACTIVITY_KEYS.ALERTS.DELETE_MODAL.CANCEL)
            setIsConfirmAlertsRemovePopupShown(false)
          }}
          onConfirm={handleRemoveAlerts}
        />
      )}
    </Box>
  )
}

export default memo(ManageTable)
